import { useState, useEffect } from 'react';
import { useQuery, useStore } from '../../../../../store/mst';
import { VisitDtoModelType } from '../../../../../store/mst/models';
import SelectedVisitQuery from '../../../../../store/mst/queries/visits/SelectedVisitQuery';
import { OrderDetailsHookParams, VisitDetailsState } from '../types';

export const useVisitDetails = ({ projectId, visitId, orderUuid }: OrderDetailsHookParams): VisitDetailsState => {
  const { model } = useStore();
  const [hasMountedPanelMode, setHasMountedPanelMode] = useState<boolean>(false);
  const [requestedOrderKey, setRequestedOrderKey] = useState<string>();
  const [hydratedOrderKeys, setHydratedOrderKeys] = useState<ReadonlySet<string>>(() => new Set());
  const [attempt, setAttempt] = useState(0);
  const orderKey = projectId && (orderUuid || visitId) ? `${projectId}:${orderUuid || visitId}` : undefined;
  const selectedVisitQuery = useQuery<{ orderByUuid?: VisitDtoModelType; visitById?: VisitDtoModelType[] }>();

  useEffect(() => {
    if (!model.isWorkspaceRouteLoading && !hasMountedPanelMode) {
      setHasMountedPanelMode(true);
    }
  }, [model.isWorkspaceRouteLoading, hasMountedPanelMode]);

  useEffect(() => {
    if (projectId && orderKey) {
      setRequestedOrderKey(orderKey);
      selectedVisitQuery.setQuery(
        orderUuid
          ? model.queryOrderByUuid({ projectId, uuid: orderUuid }, SelectedVisitQuery, { fetchPolicy: 'cache-and-network' })
          : model.queryVisitById({ projectId, visitId }, SelectedVisitQuery, { fetchPolicy: 'cache-and-network' }),
      );
    }
  }, [model, orderKey, attempt]);

  useEffect(() => {
    if (!requestedOrderKey || requestedOrderKey !== orderKey || selectedVisitQuery.loading) {
      return;
    }

    setHydratedOrderKeys((previous) => {
      if (previous.has(requestedOrderKey)) {
        return previous;
      }
      const next = new Set(previous);
      next.add(requestedOrderKey);
      return next;
    });
  }, [requestedOrderKey, orderKey, selectedVisitQuery.loading]);

  return {
    visit: requestedOrderKey === orderKey && selectedVisitQuery.data?.orderByUuid?.uuid === orderUuid
      ? selectedVisitQuery.data?.orderByUuid : undefined,
    retry: () => setAttempt(value => value + 1),
    loading: Boolean(orderKey && requestedOrderKey !== orderKey) || selectedVisitQuery.loading,
    hasMountedPanelMode,
    isInitialLoad: Boolean(orderKey && !hydratedOrderKeys.has(orderKey)),
  };
};
